import { Component, computed, inject, Injector, Input, OnInit, Signal, signal, TemplateRef, ViewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, UntypedFormArray, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { take } from 'rxjs/operators';

import { AlertService, Modal, ThemeService } from '../../../global';
import { BaseFormControl } from '../base-control';
import { FormScheduleTimelineField } from '../form-field';
import {
    blockMinutes,
    DEFAULT_GENERATOR_MINUTES,
    generateSchedule,
    groupBlocksByDay,
    minutesToPixels,
    moveBlock,
    pixelsToMinutes,
    PIXELS_PER_MINUTE,
    resizeBlock,
    scheduleEnd,
    shiftScheduleTo,
    swapThresholdPx,
    TimeAxisTick,
    timeAxisTicks
} from '../../../event/schedule-plan';
import {
    blockTimeRange,
    cascadeScheduleStarts,
    dateOf,
    isFirstBlockOfDay,
    isLastBlockOfDay,
    nextScheduleBlockDefaults,
    ScheduleBlockValue,
    scheduleBlockTitle,
    timeOf
} from '../../../event/schedule-blocks';

/**
 * A pointer that moves less than this before release is a tap, not a drag — the
 * same figure (and the same reasoning) this app's original drag-to-reorder grip
 * used before it was replaced with buttons: fine enough to not swallow a real
 * tap, coarse enough that a finger settling by a pixel or two on the way to
 * lifting off is never read as an attempted reorder.
 */
const REORDER_THRESHOLD_PX = 8;

interface TimelineRow {
    index: number;
    label: string;
    timeRange: string;
    heightPx: number;
    type: string;
    /** Shown on the block itself, so the organiser can see which blocks carry a note without
     *  opening each one. Clamped to the room the block has - see .schedule-block-description. */
    description?: string;
}

/**
 * One rendered day column: its own heading, its own time axis (scaled to that
 * day's blocks only), and its own rows. `rows[].index` is still an index into the
 * FLAT `blocks()` array — see the `dayColumns` computed below — never a position
 * within this column, so selection/resize/move stay correct once there is more
 * than one day.
 */
interface TimelineDayColumn {
    date: Date;
    dateLabel: string;
    timeAxis: TimeAxisTick[];
    rows: TimelineRow[];
}

@Component({
    standalone: false,
    selector: 'abs-schedule-timeline',
    template: `
        <div class="schedule-timeline">
            <label *ngIf="!field.hideLabel && field.label"
                >{{ field.label }}
                <ion-icon class="info" *ngIf="field.description" name="information-circle" (click)="showDescription($event)"></ion-icon
            ></label>

            <div class="schedule-timeline-head">
                <span>{{ summary() }}</span>
                <ion-button class="btn-sm" (click)="addBlock()">Add block</ion-button>
            </div>

            @if (!blocks().length) {
                <p class="schedule-timeline-empty">No schedule yet. Use "Build a schedule" above, or add blocks one at a time.</p>
            }

            <div class="schedule-timeline-controls">
                <ion-button class="btn-sm" (click)="generate()">Generate schedule</ion-button>
                @if (blocks().length) {
                    <label class="schedule-timeline-start">
                        Day starts at
                        <input type="time" [value]="startTimeValue()" (change)="setStartTime($any($event).target.value)" />
                    </label>
                }
            </div>

            @if (strandedBlockCount(); as count) {
                <p class="schedule-timeline-warning">
                    {{ count }} block{{ count === 1 ? '' : 's' }} fall outside the event's current date range and won't be shown below.
                    Widen the event's dates to bring them back, then open a block to change or delete it.
                </p>
            }

            <!-- Suppressed entirely while the schedule is empty: one empty dated column
                 per day of the event, sitting under the "No schedule yet" message, said
                 the same thing twice and left the organiser with a row of headings and
                 nothing beneath them. A day that is empty while OTHER days have blocks
                 still renders — that one is information (see groupBlocksByDay). -->
            @if (blocks().length) {
                <div class="schedule-timeline-days">
                    @for (day of dayColumns(); track day.date.getTime()) {
                        <div class="schedule-day-column">
                            <div class="schedule-day-heading">{{ day.dateLabel }}</div>

                            <div class="schedule-timeline-body">
                                <div class="schedule-timeline-axis">
                                    @for (tick of day.timeAxis; track tick.offsetPx) {
                                        <span class="schedule-timeline-axis-tick" [style.top.px]="tick.offsetPx">{{ tick.label }}</span>
                                    }
                                </div>

                                <div class="schedule-timeline-track">
                                    @for (row of day.rows; track row.index) {
                                        <div
                                            class="schedule-block schedule-block--{{ row.type }}"
                                            [class.schedule-block--selected]="row.index === selectedIndex()"
                                            [class.schedule-block--dragging]="row.index === draggingIndex()"
                                            [style.height.px]="row.heightPx"
                                            (click)="onBlockClick(row.index)"
                                            (pointerdown)="startBlockDrag($event, row.index)"
                                            (pointermove)="onBlockPointerMove($event)"
                                            (pointerup)="onBlockPointerUp()"
                                            (pointercancel)="onBlockPointerCancel()"
                                        >
                                            <div class="schedule-block-title">{{ row.label }}</div>
                                            <div class="schedule-block-time">{{ row.timeRange }}</div>
                                            <!-- On the block rather than only in the edit modal:
                                                 an organiser checking a day's scenarios should not
                                                 have to open five blocks to read five notes. The
                                                 block is sized by duration, so this is clamped to
                                                 whatever room is left under the title and time. -->
                                            @if (row.description) {
                                                <div class="schedule-block-description">{{ row.description }}</div>
                                            }
                                            <div
                                                class="schedule-block-resize"
                                                (pointerdown)="startResize($event, row.index)"
                                                (pointermove)="onPointerMove($event)"
                                                (pointerup)="onPointerUp()"
                                                (pointercancel)="onPointerCancel()"
                                            ></div>
                                        </div>
                                    }
                                </div>
                            </div>
                        </div>
                    }
                </div>
            }

            <ng-template #blockModal>
                @if (selectedBlock(); as selected) {
                    <!-- base.scss scopes .form-group/label/ion-input/ion-select styling under
                         "form, .form" — this control isn't inside a <form> (it writes directly
                         to the selected block, not via [formGroup]), so a bare .form-group here
                         would render unstyled. The .form wrapper is what makes "house field
                         styling" actually apply; it adds no new CSS. -->
                    <div class="form schedule-block-form">
                        <div class="form-group">
                            <label>Label</label>
                            <ion-input [value]="selected.label" (ionInput)="setLabel($any($event).target.value)"></ion-input>
                        </div>
                        <div class="form-group">
                            <label>Type</label>
                            <ion-select
                                interface="popover"
                                [interfaceOptions]="{ cssClass: 'popover-wide' }"
                                [value]="selected.type"
                                (ionChange)="setType($any($event).detail.value)"
                            >
                                <ion-select-option value="registration">Registration</ion-select-option>
                                <ion-select-option value="round">Round</ion-select-option>
                                <ion-select-option value="break">Break</ion-select-option>
                                <ion-select-option value="results">Results &amp; Prizes</ion-select-option>
                                <ion-select-option value="day_end">Day End</ion-select-option>
                            </ion-select>
                        </div>
                        <div class="form-group">
                            <label>Duration (minutes)</label>
                            <ion-input
                                type="number"
                                [value]="selected.durationMinutes"
                                (ionChange)="setDuration($any($event).target.value)"
                            ></ion-input>
                        </div>
                        <div class="form-group">
                            <label>Description</label>
                            <!-- autoGrow so a scenario that runs to a paragraph is readable while
                                 being written, rather than through a two-line porthole. -->
                            <ion-textarea
                                [autoGrow]="true"
                                rows="2"
                                placeholder="Scenario, house rules, anything players need for this block"
                                [value]="selected.description"
                                (ionInput)="setDescription($any($event).target.value)"
                            ></ion-textarea>
                        </div>
                        <!-- base.scss's own button container, so these sit on the same
                             grid and gap as every other button group in the app rather
                             than on a bespoke row with no styling of its own. -->
                        <div class="buttons">
                            <ion-button class="btn-sm" [disabled]="!canMoveSelectedUp()" (click)="moveSelected(-1)">Move up</ion-button>
                            <ion-button class="btn-sm" [disabled]="!canMoveSelectedDown()" (click)="moveSelected(1)">
                                Move down
                            </ion-button>
                        </div>
                        <div class="buttons">
                            <ion-button class="btn-sm" color="danger" fill="outline" (click)="deleteAndClose()">Delete block</ion-button>
                        </div>
                    </div>
                }
            </ng-template>
        </div>
    `
})
export class ScheduleTimelineComponent extends BaseFormControl implements OnInit {
    @Input()
    field: FormScheduleTimelineField;

    private readonly alert = inject(AlertService);
    private readonly modal = inject(Modal);
    private readonly themeService = inject(ThemeService);
    private readonly injector = inject(Injector);

    readonly selectedIndex = signal<number | null>(null);
    /** Bumped on every write so the computed views re-read the control's value. */
    private readonly version = signal(0);

    /**
     * The root event form's own value, read reactively. Built in ngOnInit rather
     * than as a field initializer, and given this component's own injector
     * explicitly, because BaseFormControl only sets parentFormGroup (and the
     * formValue$ observable this wraps) in ITS ngOnInit — neither exists yet at
     * field-initializer time, which is the only place toSignal's automatic
     * injection-context detection would otherwise work.
     */
    private formValues!: Signal<any>;

    /**
     * In-progress resize state, or null when no drag is active. Tracking the
     * pointer's starting Y and the block's starting duration (rather than
     * accumulating deltas move-to-move) means a slightly missed pointermove
     * never compounds into drift — every move recomputes from the same anchor.
     */
    private resize: { index: number; startY: number; startMinutes: number } | null = null;
    /**
     * Live block state while a resize is in progress. Rendered in place of
     * blocks() so the block visibly follows the finger, but never written
     * through the form control — control.setValue on every pointermove would
     * re-run validators up the whole form tree once per frame. The committed
     * value only reaches the control in onPointerUp, via the same write() every
     * other mutation uses.
     */
    private readonly dragPreview = signal<ScheduleBlockValue[] | null>(null);

    /** Set true by onPointerUp after a resize, so the click the browser fires
     * immediately afterwards is swallowed rather than re-running select(). */
    private suppressClick = false;

    /**
     * In-progress whole-block reorder drag, or null. `index` is the block's
     * current position in the flat array — updated in place on every swap, so it
     * always reflects where the dragged block actually is, not where the drag
     * started. `moved` becomes true the first time the pointer crosses
     * REORDER_THRESHOLD_PX; a release before that is a tap (opens the block
     * editor) rather than a reorder.
     */
    private blockDrag: { index: number; startY: number; lastSwapY: number; moved: boolean } | null = null;

    /**
     * The block currently being dragged, for the `--dragging` style. A signal
     * (not read off blockDrag directly) so the template updates the moment
     * dragging starts or ends, the same way dragPreview drives every other
     * live-drag render.
     */
    readonly draggingIndex = signal<number | null>(null);
    /** Passed to Modal.show() as contentTemplate — see select(). */
    @ViewChild('blockModal') private blockModalRef!: TemplateRef<any>;

    // Fields are keyed literally by name (a field named 'a.b' is one flat control
    // keyed 'a.b', not a nested group), and AbstractControl.get() splits on '.' and
    // silently returns null. controls[key] indexes the literal key directly.
    get control(): UntypedFormControl | UntypedFormArray {
        return (this.parentFormGroup as UntypedFormGroup).controls[this.field.name] as UntypedFormControl;
    }

    readonly blocks = computed<ScheduleBlockValue[]>(() => {
        this.version();
        return (this.control?.value as ScheduleBlockValue[]) ?? [];
    });

    /**
     * Blocks grouped into one entry per calendar day of the event, from the
     * event's own startDate/endDate — not schedule-timeline-local fields, so
     * they come off the root form the same way generate() reads its other
     * event-level fields. Reads dragPreview() ?? blocks() (rather than blocks()
     * alone) so a resize in progress regroups live instead of visibly snapping
     * back to the committed layout mid-drag.
     *
     * Reads formValues() (the root form's value as a signal) rather than a plain
     * `.value` snapshot: a plain read is not a signal subscription, so this
     * computed's only real dependencies were dragPreview()/blocks() and editing
     * startDate/endDate alone — without also touching the schedule — never
     * triggered a recompute. Shortening the date range silently failed to
     * surface the stranded-blocks warning, and widening it back out silently
     * failed to bring blocks back into view, until something else (generate,
     * resize, move, delete) forced a recompute.
     */
    readonly dayGroups = computed(() => {
        const form = this.formValues() ?? {};
        return groupBlocksByDay(this.dragPreview() ?? this.blocks(), form.startDate, form.endDate);
    });

    /**
     * Render-ready columns: one per day group, each with its own time axis
     * (scaled to that day's blocks only) and its own rows.
     *
     * `row.index` is deliberately an index into the FLAT `dragPreview() ?? blocks()`
     * array, found via indexOf against that same array dayGroups() was built
     * from — groupBlocksByDay returns the actual block objects, not copies, so
     * reference equality here always finds the right flat position. Every other
     * part of this component (selectedIndex, startResize, moveSelected,
     * deleteSelected...) keys a block by that same flat index, so a column's row
     * index must resolve there too, never to a position within the column.
     */
    readonly dayColumns = computed<TimelineDayColumn[]>(() => {
        const flat = this.dragPreview() ?? this.blocks();
        return this.dayGroups().map(
            (group): TimelineDayColumn => ({
                date: group.date,
                dateLabel: dateOf(group.date),
                timeAxis: timeAxisTicks(group.blocks),
                rows: group.blocks.map(
                    (block): TimelineRow => ({
                        index: flat.indexOf(block),
                        type: block.type ?? 'break',
                        label: scheduleBlockTitle(block),
                        timeRange: blockTimeRange(block),
                        description: block.description,
                        heightPx: minutesToPixels(blockMinutes(block), PIXELS_PER_MINUTE)
                    })
                )
            })
        );
    });

    /**
     * Blocks present in the flat array but absent from every day group — i.e.
     * outside the event's current date range, most often because the organiser
     * shortened startDate/endDate after generating or adding blocks.
     * groupBlocksByDay never drops or clamps these; they stay in the stored
     * array untouched. This is only the count that drives the warning banner —
     * see the class doc on the banner's markup for the "warn, don't drop" choice.
     *
     * Compares against dragPreview() ?? blocks() — the same flat array
     * dayGroups() was built from, matching dayColumns() above — rather than
     * blocks() alone: during a resize, resizeBlock returns fresh object
     * references for the resized block and everything cascaded after it, so
     * comparing the committed blocks() against a dayGroups() built off the live
     * dragPreview() failed reference equality for every one of those blocks and
     * miscounted them as stranded mid-drag.
     */
    readonly strandedBlockCount = computed(() => {
        const flat = this.dragPreview() ?? this.blocks();
        const grouped = new Set(this.dayGroups().flatMap((g) => g.blocks));
        return flat.filter((b) => !grouped.has(b)).length;
    });

    readonly selectedBlock = computed<ScheduleBlockValue | null>(() => {
        const index = this.selectedIndex();
        return index === null ? null : (this.blocks()[index] ?? null);
    });

    /**
     * Move up / Move down stop at a calendar-day boundary, not just at the ends of
     * the flat array.
     *
     * Reordering across a boundary is a no-op the organiser cannot see: the cascade
     * refuses to re-time across days (see cascadeScheduleStarts), so the moved block
     * keeps its own day's start time, stays in its own column, and only its position
     * in the stored array changes. A button that visibly does nothing is worse than
     * one that is visibly unavailable, and each day is its own ordered column on
     * screen anyway — so "move" means "move within this day".
     */
    readonly canMoveSelectedUp = computed(() => {
        const index = this.selectedIndex();
        return index !== null && !isFirstBlockOfDay(this.blocks(), index);
    });

    readonly canMoveSelectedDown = computed(() => {
        const index = this.selectedIndex();
        return index !== null && !isLastBlockOfDay(this.blocks(), index);
    });

    readonly summary = computed(() => {
        const blocks = this.blocks();
        if (!blocks.length) {
            return 'Schedule';
        }
        const end = scheduleEnd(blocks);
        const endLabel = end ? timeOf(end) : '';
        return `${blocks.length} blocks, ending ${endLabel}`;
    });

    readonly startTimeValue = computed(() => {
        const first = this.blocks()[0];
        const start = first?.startTime ? new Date(first.startTime) : null;
        if (!start) {
            return '';
        }
        return timeOf(start);
    });

    ngOnInit() {
        super.ngOnInit();
        // this.formValue$ (root.valueChanges) only exists once super.ngOnInit() has
        // run, so this can't be a field initializer — toSignal is given this
        // component's own injector explicitly rather than relying on automatic
        // injection-context detection, which only covers construction time.
        this.formValues = toSignal(this.formValue$, { injector: this.injector, initialValue: this.parentFormGroup.root.value });
        this.seedGeneratorInputs();
    }

    /**
     * Puts the generator's defaults on screen instead of four blank boxes.
     *
     * A form field's `defaultValue` is only applied when *creating* a record, so on
     * an edit these controls load null: the generator still used 120/15/30/15
     * internally, but the organiser had to guess what numbers they were about to
     * get. Seeded here rather than in the form config because that is where the
     * defaults table is now (DEFAULT_GENERATOR_MINUTES), and because `generatorGames`
     * comes from the event's own numberOfRounds rather than from a constant.
     *
     * Nothing later clobbers this: `patchForm` only ever `patchValue`s the saved
     * event — it never resets the form — and these controls are deliberately not
     * event fields, so the API's allowlist drops them and they are never in that
     * data. `numberOfRounds` is the exception: it *is* event data and arrives with
     * the patch, which may be after this runs, hence waiting for its first emission.
     */
    private seedGeneratorInputs() {
        // Spelled out rather than derived from the keys of DEFAULT_GENERATOR_MINUTES,
        // so each control name is greppable from the form config that declares it.
        const minuteFields: [string, number][] = [
            ['generatorGameMinutes', DEFAULT_GENERATOR_MINUTES.gameMinutes],
            ['generatorBreakMinutes', DEFAULT_GENERATOR_MINUTES.breakMinutes],
            ['generatorRegistrationMinutes', DEFAULT_GENERATOR_MINUTES.registrationMinutes],
            ['generatorResultsMinutes', DEFAULT_GENERATOR_MINUTES.resultsMinutes]
        ];
        minuteFields.forEach(([name, minutes]) => this.seedIfEmpty(this.generatorControl(name), minutes));

        const games = this.generatorControl('generatorGames');
        const rounds = this.generatorControl('numberOfRounds');
        if (!games || !rounds) {
            return;
        }

        this.seedIfEmpty(games, rounds.value);
        rounds.valueChanges.pipe(take(1)).subscribe((value) => this.seedIfEmpty(games, value));
    }

    // Controls are keyed literally by field name, so controls[key] — never get(key),
    // which splits on '.' and returns null. The generator inputs live in a nested
    // form *section*, but createForm flattens every section into the one root group,
    // so they are all siblings of this control's own.
    private generatorControl(name: string): AbstractControl | null {
        return (this.parentFormGroup.root as UntypedFormGroup).controls[name] ?? null;
    }

    /** Only ever fills a blank, so a number the organiser has chosen (or cleared to
     * zero) is never overwritten. TextboxComponent writes null for a blank number. */
    private seedIfEmpty(control: AbstractControl | null, value: number | null | undefined) {
        if (!control || value === null || value === undefined) {
            return;
        }
        if (control.value === null || control.value === undefined || control.value === '') {
            control.setValue(value);
        }
    }

    /**
     * Reads the generator inputs off the root form. They are deliberately not event
     * fields — the API's allowlist drops them — so they exist only to drive this.
     */
    async generate() {
        const form = this.parentFormGroup.root.value ?? {};
        const existing = this.blocks();

        if (existing.length) {
            const result = await this.alert.showConfirm(
                'Replace schedule?',
                `This replaces the ${existing.length} blocks already in the schedule.`,
                { ok: 'Replace', cancel: 'Cancel' }
            );
            if (result.role !== 'ok') {
                return;
            }
        }

        // 09:00 on each of the event's own dates, always. There is no "day starts at"
        // input here on purpose: the one this used to read self-populated with the
        // current date and time on init, so a day for an event eight months away was
        // generated on today's date and the deferred default below never ran. Moving
        // a day is what the timeline's own "Day starts at" control is for, and one
        // control for one concept is the point.
        //
        // The ?? fallbacks are belt and braces: seedGeneratorInputs has already put
        // these numbers in the controls, so normally the form supplies them.
        this.write(
            generateSchedule({
                games: Number(form.generatorGames ?? form.numberOfRounds ?? 3),
                gameMinutes: Number(form.generatorGameMinutes ?? DEFAULT_GENERATOR_MINUTES.gameMinutes),
                breakMinutes: Number(form.generatorBreakMinutes ?? DEFAULT_GENERATOR_MINUTES.breakMinutes),
                registrationMinutes: Number(form.generatorRegistrationMinutes ?? DEFAULT_GENERATOR_MINUTES.registrationMinutes),
                resultsMinutes: Number(form.generatorResultsMinutes ?? DEFAULT_GENERATOR_MINUTES.resultsMinutes),
                startDate: form.startDate,
                endDate: form.endDate
            })
        );
        this.selectedIndex.set(null);
    }

    /** Moves the whole day, keeping every block's length and order. */
    setStartTime(value: string) {
        const [hours, minutes] = (value ?? '').split(':').map((n) => parseInt(n, 10));
        const first = this.blocks()[0];
        if (!first?.startTime || isNaN(hours)) {
            return;
        }

        const anchor = new Date(first.startTime);
        anchor.setHours(hours, minutes || 0, 0, 0);

        this.write(shiftScheduleTo(this.blocks(), anchor));
    }

    select(index: number) {
        const wasSelected = this.selectedIndex() === index;
        this.selectedIndex.set(wasSelected ? null : index);
        if (!wasSelected) {
            this.modal.show({
                props: {
                    title: 'Edit block',
                    contentTemplate: this.blockModalRef,
                    cssClass: `${this.themeService.theme()} no-background`,
                    afterDismiss: () => this.selectedIndex.set(null)
                }
            });
        }
    }

    /**
     * The click bound on the block body, in front of select(). A resize ends with
     * a pointerup that the browser follows with a click on the same element —
     * onPointerUp sets suppressClick for exactly that click, so finishing a
     * resize never also reopens (or closes) the block editor modal for whatever
     * now sits under the pointer.
     */
    onBlockClick(index: number) {
        if (this.suppressClick) {
            this.suppressClick = false;
            return;
        }
        this.select(index);
    }

    /**
     * Pointer events rather than separate mouse and touch handlers: one code path
     * for mouse, finger and pen. setPointerCapture keeps the move events coming to
     * this element even when the pointer slides off it, which happens constantly
     * when dragging a short block taller.
     */
    startResize(event: PointerEvent, index: number) {
        event.preventDefault();
        event.stopPropagation();

        const block = this.blocks()[index];
        if (!block) {
            return;
        }

        (event.target as HTMLElement).setPointerCapture?.(event.pointerId);
        this.resize = { index, startY: event.clientY, startMinutes: blockMinutes(block) };
    }

    onPointerMove(event: PointerEvent) {
        if (!this.resize) {
            return;
        }
        event.preventDefault();

        const deltaMinutes = pixelsToMinutes(event.clientY - this.resize.startY, PIXELS_PER_MINUTE);
        this.dragPreview.set(resizeBlock(this.blocks(), this.resize.index, this.resize.startMinutes + deltaMinutes));
    }

    onPointerUp() {
        if (this.resize) {
            const preview = this.dragPreview();
            if (preview) {
                this.write(preview);
            }
            // The browser follows the release with a compatibility click, and because
            // the pointer is captured that click is retargeted — it lands on the
            // *block*, whose (click) opens the block editor modal. It fires even though
            // pointerdown was preventDefault()ed and stopPropagation()ed, and even
            // when the pointer is released well outside the block. Without this every
            // resize also toggled the editor: drag once to open it, drag again to shut
            // it. suppressClick exists for exactly this.
            this.suppressClick = true;
        }
        this.resize = null;
        this.dragPreview.set(null);
    }

    /**
     * pointercancel means abort, not commit — the browser has taken the gesture over
     * (a pinch, a page scroll it decided to own) or the pointer stopped existing, so
     * the live preview is discarded rather than written.
     *
     * It must also leave suppressClick alone. A cancel is never followed by a click,
     * so a flag set here has nothing to consume it and would instead silently eat the
     * user's next genuine tap on any block.
     */
    onPointerCancel() {
        this.resize = null;
        this.dragPreview.set(null);
    }

    /**
     * Picking up a block to reorder it. Whether this turns into a drag or stays a
     * tap (which onBlockClick opens the editor for) isn't decided here — only
     * once the pointer has moved past REORDER_THRESHOLD_PX. Captured on
     * currentTarget rather than target: the block has child elements (title,
     * time, the resize strip), and a press that lands on one of those must still
     * capture the block itself, not whichever child happened to be under the
     * finger.
     *
     * A press that starts on the resize strip never reaches here — startResize
     * calls stopPropagation(), so this handler simply isn't invoked for it, and
     * the two gestures can never both be live at once.
     */
    startBlockDrag(event: PointerEvent, index: number) {
        event.preventDefault();
        (event.currentTarget as HTMLElement).setPointerCapture?.(event.pointerId);
        this.blockDrag = { index, startY: event.clientY, lastSwapY: event.clientY, moved: false };
    }

    /**
     * Swap-in-place, not a floating ghost: the dragged block is always whichever
     * one is currently sitting in the slot it would land in if released right
     * now, so there is no separate drop-target indicator to keep in sync — the
     * reordered list and the drag preview are the same render. A swap only
     * registers once the pointer has travelled past half of the *next* block's
     * own height (swapThresholdPx), so a fast drag across several short blocks
     * still needs a sensible amount of travel per block, not merely a raw
     * distance from the drag's start.
     *
     * Never crosses a calendar-day boundary — isFirstBlockOfDay/isLastBlockOfDay
     * are the same guards Move up/down use, so a drag that reaches the edge of
     * its own day simply stops there instead of reaching into the next column.
     */
    onBlockPointerMove(event: PointerEvent) {
        const drag = this.blockDrag;
        if (!drag) {
            return;
        }
        event.preventDefault();

        if (!drag.moved && Math.abs(event.clientY - drag.startY) < REORDER_THRESHOLD_PX) {
            return;
        }
        drag.moved = true;
        this.draggingIndex.set(drag.index);

        const blocks = this.dragPreview() ?? this.blocks();
        const sinceLastSwap = event.clientY - drag.lastSwapY;

        if (sinceLastSwap > 0 && !isLastBlockOfDay(blocks, drag.index)) {
            const next = blocks[drag.index + 1];
            if (next && sinceLastSwap >= swapThresholdPx(next, PIXELS_PER_MINUTE)) {
                this.dragPreview.set(moveBlock(blocks, drag.index, drag.index + 1));
                drag.index += 1;
                drag.lastSwapY = event.clientY;
                this.draggingIndex.set(drag.index);
            }
        } else if (sinceLastSwap < 0 && !isFirstBlockOfDay(blocks, drag.index)) {
            const prev = blocks[drag.index - 1];
            if (prev && -sinceLastSwap >= swapThresholdPx(prev, PIXELS_PER_MINUTE)) {
                this.dragPreview.set(moveBlock(blocks, drag.index, drag.index - 1));
                drag.index -= 1;
                drag.lastSwapY = event.clientY;
                this.draggingIndex.set(drag.index);
            }
        }
    }

    /**
     * A release that never crossed the threshold is a tap: leave suppressClick
     * alone so onBlockClick's own (click) binding runs normally and opens the
     * block editor. A release that did drag commits the live preview (the same
     * write() every other mutation uses) and sets suppressClick, for the same
     * browser-issued compatibility click onPointerUp already has to swallow
     * after a resize.
     */
    onBlockPointerUp() {
        const drag = this.blockDrag;
        if (drag?.moved) {
            const preview = this.dragPreview();
            if (preview) {
                this.write(preview);
            }
            this.suppressClick = true;
        }
        this.blockDrag = null;
        this.draggingIndex.set(null);
        this.dragPreview.set(null);
    }

    /** Abort, not commit — mirrors onPointerCancel's own reasoning exactly. */
    onBlockPointerCancel() {
        this.blockDrag = null;
        this.draggingIndex.set(null);
        this.dragPreview.set(null);
    }

    /** Single place that writes back, so every mutation re-renders the same way. */
    private write(blocks: ScheduleBlockValue[]) {
        this.control.setValue(blocks);
        this.version.update((v) => v + 1);
    }

    private updateSelected(change: Partial<ScheduleBlockValue>) {
        const index = this.selectedIndex();
        if (index === null) {
            return;
        }
        const blocks = [...this.blocks()];
        blocks[index] = { ...blocks[index], ...change };
        this.write(blocks);
    }

    setLabel(label: string) {
        this.updateSelected({ label });
    }

    setType(type: string) {
        this.updateSelected({ type });
    }

    setDescription(description: string) {
        // Stored as undefined rather than '' when cleared, so a block the organiser emptied
        // reads the same as one that never had a description and no reader has to test for both.
        this.updateSelected({ description: description?.trim() ? description : undefined });
    }

    setDuration(value: string) {
        const index = this.selectedIndex();
        if (index === null) {
            return;
        }
        // resizeBlock both sets the duration and re-times everything after it —
        // never write start times here. It also clamps non-finite and
        // sub-minimum values itself, so parseInt's NaN on an empty or partial
        // value is handled there, not here — clamping twice is what let a
        // mid-typing keystroke get committed and then overwritten by the
        // clamped value on the next one.
        this.write(resizeBlock(this.blocks(), index, parseInt(value, 10)));
    }

    addBlock() {
        const defaults = nextScheduleBlockDefaults(this.blocks(), this.parentFormGroup.root.value?.startDate);
        this.write([...this.blocks(), { label: 'New block', type: 'break', ...defaults }]);
        this.selectedIndex.set(this.blocks().length - 1);
    }

    moveSelected(delta: 1 | -1) {
        const index = this.selectedIndex();
        if (index === null) {
            return;
        }
        const target = index + delta;
        this.write(moveBlock(this.blocks(), index, target));
        this.selectedIndex.set(target);
    }

    deleteSelected() {
        const index = this.selectedIndex();
        if (index === null) {
            return;
        }
        // Removing a block leaves the ones after it starting where the deleted
        // block used to — cascade from its predecessor (or the new first block)
        // so every minute of the day still belongs to a block.
        const remaining = this.blocks().filter((_, i) => i !== index);
        this.write(cascadeScheduleStarts(remaining, Math.max(index - 1, 0)));
        this.selectedIndex.set(null);
    }

    /** deleteSelected() already clears selectedIndex itself; the explicit dismiss()
     * here is belt-and-braces consistency with the modal's own dismiss path, not a
     * second source of truth. */
    deleteAndClose() {
        this.deleteSelected();
        this.modal.dismiss();
    }
}
